import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { monotoneCubicArea, monotoneCubicPath } from './smoothCurve.ts';

export interface HistoryPoint {
  month: string;
  label: string;
  portfolio: number;
  selic: number | null;
  portfolioValue?: number;
  /** Month portfolio return minus month SELIC return, in percentage points. */
  vsSelicPct?: number | null;
  /** Realized SELIC in that month, percent per month (1.04 = 1,04% a.m.). */
  selicMonthRatePct?: number | null;
}

interface Props {
  points: HistoryPoint[];
  selicLabel?: string;
  empty?: boolean;
}

function formatIndex(v: number): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Signed pt-BR percent: +4,0% or −2,1% */
export function formatVsSelic(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  if (v > 0) return `+${abs}%`;
  if (v < 0) return `−${abs}%`;
  return '0,0%';
}

/** pt-BR monthly SELIC rate plus compound annual equivalent. */
export function formatSelicMonthRate(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '—';
  const am = v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const aa = (Math.pow(1 + v / 100, 12) - 1) * 100;
  const aaLabel = aa.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${am}% a.m. (equiv. ${aaLabel}% a.a.)`;
}

/** Area/line carteira + dashed SELIC — ambas em índice 100 desde o 1º ponto com valor. */
const PortfolioHistoryChart: React.FC<Props> = ({
  points,
  selicLabel = 'SELIC (índice)',
  empty,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [plotW, setPlotW] = useState(640);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      if (w >= 280) setPlotW(prev => (prev === w ? prev : w));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const signature = useMemo(
    () =>
      points
        .map(
          p =>
            `${p.month}:${p.portfolio}:${p.selic ?? ''}:${p.vsSelicPct ?? ''}:${p.selicMonthRatePct ?? ''}`
        )
        .join('|'),
    [points]
  );

  const geo = useMemo(() => {
    const w = Math.max(320, plotW);
    const h = 228;
    const padL = 72;
    const padR = 16;
    const padT = 16;
    const padB = 46;
    const innerW = w - padL - padR;
    const innerH = h - padT - padB;

    if (!points.length) {
      return {
        w,
        h,
        padL,
        padR,
        padT,
        padB,
        innerW,
        innerH,
        portfolioPath: '',
        areaPath: '',
        selicPath: '',
        yTicks: [] as { y: number; label: string }[],
        xLabels: [] as { x: number; label: string; anchor: 'start' | 'middle' | 'end' }[],
        hits: [] as { i: number; x: number; width: number }[],
        coords: [] as { x: number; yP: number; yS: number | null }[],
      };
    }

    const vals = points.flatMap(p => {
      const arr = [p.portfolio];
      if (p.selic != null) arr.push(p.selic);
      return arr;
    });
    let minV = Math.min(...vals);
    let maxV = Math.max(...vals);
    const MIN_SPAN = 8;
    if (maxV - minV < MIN_SPAN) {
      const mid = (maxV + minV) / 2;
      minV = mid - MIN_SPAN / 2;
      maxV = mid + MIN_SPAN / 2;
    } else {
      const pad = (maxV - minV) * 0.12;
      minV -= pad;
      maxV += pad;
    }
    const span = maxV - minV || 1;

    const xAt = (i: number) =>
      padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const yAt = (v: number) => padT + innerH - ((v - minV) / span) * innerH;

    const portfolioCoords = points.map((p, i) => ({ x: xAt(i), y: yAt(p.portfolio) }));
    const baseY = padT + innerH;
    // Smooth curves through the monthly samples — no extra points, no new extrema.
    const portfolioPath = monotoneCubicPath(portfolioCoords);
    const areaPath = monotoneCubicArea(portfolioCoords, baseY);

    const selicCoords = points
      .map((p, i) => (p.selic != null ? { x: xAt(i), y: yAt(p.selic) } : null))
      .filter((c): c is { x: number; y: number } => c != null);
    const selicPath = monotoneCubicPath(selicCoords);

    const yTicks = [maxV, (maxV + minV) / 2, minV].map(v => ({
      y: yAt(v),
      label: formatIndex(v),
    }));

    const step = Math.max(1, Math.ceil(points.length / 6));
    const xLabels = points.map((p, i) => {
      const show = i === 0 || i === points.length - 1 || (i % step === 0 && i !== points.length - 1);
      const anchor: 'start' | 'middle' | 'end' =
        i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle';
      return { x: xAt(i), label: show ? p.label : '', anchor };
    });

    const hits = points.map((_, i) => {
      const x = xAt(i);
      const prev = i === 0 ? padL : (xAt(i - 1) + x) / 2;
      const next = i === points.length - 1 ? padL + innerW : (x + xAt(i + 1)) / 2;
      return { i, x, width: Math.max(4, next - prev) };
    });

    const coords = points.map((p, i) => ({
      x: xAt(i),
      yP: yAt(p.portfolio),
      yS: p.selic != null ? yAt(p.selic) : null,
    }));

    return {
      w,
      h,
      padL,
      padR,
      padT,
      padB,
      innerW,
      innerH,
      portfolioPath,
      areaPath,
      selicPath,
      yTicks,
      xLabels,
      hits,
      coords,
    };
    // signature forces a redraw when filtered values change even if the array identity is reused
  }, [points, signature, plotW]);

  if (empty || points.length === 0) {
    return (
      <div className="history-chart" ref={wrapRef}>
        <div className="muted" style={{ padding: '40px 8px', textAlign: 'center', fontSize: 13 }}>
          Sem histórico de carteira ainda. Importe ou cadastre posições para ver a evolução.
        </div>
        <div className="chart-legend-inline">
          <span className="leg">
            <span className="leg-line" /> Carteira (índice)
          </span>
          <span className="leg">
            <span className="leg-line dashed" /> {selicLabel}
          </span>
        </div>
      </div>
    );
  }

  const hoverPt = hover != null ? points[hover] : null;
  const hoverX = hover != null ? geo.coords[hover]?.x : null;
  const tooltipLeft =
    hoverX != null ? Math.min(88, Math.max(12, (hoverX / geo.w) * 100)) : 50;

  return (
    <div ref={wrapRef}>
      <div className="history-chart history-chart-wrap">
        <svg
          viewBox={`0 0 ${geo.w} ${geo.h}`}
          preserveAspectRatio="xMinYMin meet"
          role="img"
          aria-label="Histórico da carteira vs SELIC. Eixo X: meses. Eixo Y: índice com base 100 no primeiro mês."
        >
          <defs>
            <linearGradient id="portfolioFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <text
            x={14}
            y={geo.padT + geo.innerH / 2}
            textAnchor="middle"
            fill="#475569"
            fontSize={11}
            fontWeight={600}
            fontFamily="Inter,system-ui,sans-serif"
            transform={`rotate(-90 14 ${geo.padT + geo.innerH / 2})`}
          >
            Índice (base 100)
          </text>
          {geo.yTicks.map((t, i) => (
            <g key={`y-${i}`}>
              <line
                x1={geo.padL}
                x2={geo.w - geo.padR}
                y1={t.y}
                y2={t.y}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
              <text
                x={geo.padL - 6}
                y={t.y + 3}
                textAnchor="end"
                fill="#64748b"
                fontSize={10}
                fontFamily="Inter,system-ui,sans-serif"
              >
                {t.label}
              </text>
            </g>
          ))}
          {geo.areaPath && <path d={geo.areaPath} fill="url(#portfolioFill)" />}
          {points.length > 1 && (
            <path
              d={geo.portfolioPath}
              fill="none"
              stroke="#16a34a"
              strokeWidth={2.25}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {geo.selicPath && points.filter(p => p.selic != null).length > 1 && (
            <>
              <path
                d={geo.selicPath}
                fill="none"
                stroke="#ffffff"
                strokeWidth={5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                opacity={0.9}
              />
              <path
                d={geo.selicPath}
                fill="none"
                stroke="#1d4ed8"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
          {hover != null && geo.coords[hover] && (
            <g>
              <line
                x1={geo.coords[hover].x}
                x2={geo.coords[hover].x}
                y1={geo.padT}
                y2={geo.padT + geo.innerH}
                stroke="#94a3b8"
                strokeDasharray="3 3"
              />
              <circle
                cx={geo.coords[hover].x}
                cy={geo.coords[hover].yP}
                r={4}
                fill="#fff"
                stroke="#16a34a"
                strokeWidth={2}
              />
              {geo.coords[hover].yS != null && (
                <circle
                  cx={geo.coords[hover].x}
                  cy={geo.coords[hover].yS}
                  r={4}
                  fill="#fff"
                  stroke="#1d4ed8"
                  strokeWidth={2}
                />
              )}
            </g>
          )}
          {geo.hits.map(h => (
            <rect
              key={`hit-${h.i}`}
              x={h.x - h.width / 2}
              y={geo.padT}
              width={h.width}
              height={geo.innerH}
              fill="transparent"
              onMouseEnter={() => setHover(h.i)}
              onMouseLeave={() => setHover(null)}
            >
              <title>
                {points[h.i].label}: carteira {formatIndex(points[h.i].portfolio)}
                {points[h.i].selic != null
                  ? `, índice SELIC ${formatIndex(points[h.i].selic)}`
                  : ''}
                {`, taxa SELIC do mês ${formatSelicMonthRate(points[h.i].selicMonthRatePct)}`}
                {`, no mês vs SELIC ${formatVsSelic(points[h.i].vsSelicPct)}`}
              </title>
            </rect>
          ))}
          {geo.xLabels.map(
            (x, i) =>
              x.label && (
                <text
                  key={`x-${i}`}
                  x={x.x}
                  y={geo.h - 18}
                  textAnchor={x.anchor}
                  fill="#64748b"
                  fontSize={10}
                  fontFamily="Inter,system-ui,sans-serif"
                >
                  {x.label}
                </text>
              )
          )}
          <text
            x={geo.padL + geo.innerW / 2}
            y={geo.h - 4}
            textAnchor="middle"
            fill="#475569"
            fontSize={11}
            fontWeight={600}
            fontFamily="Inter,system-ui,sans-serif"
          >
            Meses
          </text>
        </svg>
        {hoverPt && (
          <div className="history-tooltip" style={{ left: `${tooltipLeft}%` }} role="status">
            <div className="tt-month">{hoverPt.label}</div>
            <div className="tt-pos">Carteira (índice): {formatIndex(hoverPt.portfolio)}</div>
            <div className="tt-selic">
              Índice SELIC: {hoverPt.selic != null ? formatIndex(hoverPt.selic) : '—'}
            </div>
            <div className="tt-rate">Taxa SELIC do mês: {formatSelicMonthRate(hoverPt.selicMonthRatePct)}</div>
            <div
              className="tt-vs"
              style={{ color: (hoverPt.vsSelicPct ?? 0) < 0 ? '#fca5a5' : '#86efac' }}
            >
              No mês vs SELIC: {formatVsSelic(hoverPt.vsSelicPct)}
            </div>
          </div>
        )}
      </div>
      <div className="chart-legend-inline">
        <span className="leg">
          <span className="leg-line" /> Carteira (índice)
        </span>
        <span className="leg">
          <span className="leg-line dashed" /> {selicLabel}
        </span>
      </div>
    </div>
  );
};

export default PortfolioHistoryChart;
